import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  TablePagination,
  TextField,
  Grid,
  useTheme,
  useMediaQuery,
  Stack,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { toast } from 'react-toastify';
import { fetchImportedInventory } from '../../redux/actions/importedInventory/importedInventoryAction';
import 'react-toastify/dist/ReactToastify.css';

const ImportedInventoryTable = ({ startDate, endDate, setStartDate, setEndDate, onEdit, onDelete }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { importedInventory: data, loading, error } = useSelector((state) => state.importedInventory);
  const { user } = useSelector((state) => state.login);
  
  // Normalize user role to lowercase for case-insensitive comparison
  const userRole = user?.role?.toLowerCase() || '';
  const isAdmin = userRole === 'admin';
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('batch_number');
  const [statusFilter, setStatusFilter] = useState('all');
  const [referenceNoFilter, setReferenceNoFilter] = useState('');
  
  const lastToastCountRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchImportedInventory());
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Error fetching data';
        toast.error(`Error fetching data: ${errorMessage}`);
      }
    };
    fetchData();
  }, [dispatch]);

  // Show success message when data is loaded
  useEffect(() => {
    if (data && data.length > 0 && !loading) {
      if (lastToastCountRef.current !== data.length) {
        toast.success(`Loaded ${data.length} records successfully`);
        lastToastCountRef.current = data.length;
      }
    }
  }, [data, loading]);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Filter data based on search term, status, and reference number
  const filteredData = data?.filter((row) => {
    const searchValue = row[searchField]?.toString().toLowerCase() || '';
    const matchesSearch = searchValue.includes(searchTerm.toLowerCase());
    
    // Status filtering
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      const statusValue = parseInt(statusFilter);
      matchesStatus = row.batch_no_status === statusValue;
    }
    
    // Reference number filtering
    let matchesReference = true;
    if (referenceNoFilter.trim() !== '') {
      const referenceValue = row.reference_number?.toString().toLowerCase() || '';
      matchesReference = referenceValue.includes(referenceNoFilter.toLowerCase());
    }
    
    return matchesSearch && matchesStatus && matchesReference;
  });

  // Paginate data
  const paginatedData = filteredData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Mobile card view component
  const MobileCardView = ({ row }) => (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" color="primary">
            {row.batch_number || 'N/A'}
          </Typography>
          {isAdmin && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" onClick={() => onEdit && onEdit(row)}>
                <EditIcon fontSize="small" sx={{ color: '#2196f3' }}/>
              </IconButton>
              <IconButton size="small" onClick={() => onDelete && onDelete(row.id)}>
                <DeleteIcon fontSize="small" sx={{ color: '#f44336' }}/>
              </IconButton>
            </Box>
          )}
        </Box>
        
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Reference Number</Typography>
            <Typography variant="body2">{row.reference_number || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Product</Typography>
            <Typography variant="body2">{row.product || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Quantity</Typography>
            <Typography variant="body2">{row.qty || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Quantity</Typography>
            <Typography variant="body2">{row.qty || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">JO Number</Typography>
            <Typography variant="body2">{row.jo_number || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Warehouse</Typography>
            <Typography variant="body2">{row.warehouse || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Gross Weight</Typography>
            <Typography variant="body2">{row.gross_weight || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Net Weight</Typography>
            <Typography variant="body2">{row.net_weight || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">JO Date</Typography>
            <Typography variant="body2">{row.jo_date ? new Date(row.jo_date).toLocaleDateString() : '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Batch Status</Typography>
            <Typography variant="body2">
              {row.batch_no_status === 0 ? 'Pending' : 
               row.batch_no_status === 1 ? 'In Progress' : 
               row.batch_no_status === 2 ? 'Completed' : '-'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Responsive Search Bar */}
      <Box sx={{ mb: 2 }}>
        {isSmallScreen ? (
          <Stack spacing={2}>
            <TextField
              select
              label="Search Field"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              SelectProps={{ native: true }}
              fullWidth
            >
              <option value="batch_number">Batch Number</option>
              <option value="product">Product</option>
              <option value="jo_number">JO Number</option>
              <option value="warehouse">Warehouse</option>
            </TextField>
            <TextField
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="0">Pending</MenuItem>
                <MenuItem value="1">In Progress</MenuItem>
                <MenuItem value="2">Completed</MenuItem>
              </Select>
            </FormControl>
            <TextField 
              label="Reference Number" 
              variant="outlined" 
              value={referenceNoFilter} 
              onChange={(e) => setReferenceNoFilter(e.target.value)} 
              fullWidth 
            />
            <TextField label="Start Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={startDate} onChange={e => setStartDate(e.target.value)} />
            <TextField label="End Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={endDate} onChange={e => setEndDate(e.target.value)} />

          </Stack>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              select
              label="Search Field"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: 150 }}
            >
              <option value="batch_number">Batch Number</option>
              <option value="product">Product</option>
              <option value="jo_number">JO Number</option>
              <option value="warehouse">Warehouse</option>
            </TextField>
            <TextField
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="0">Pending</MenuItem>
                <MenuItem value="1">In Progress</MenuItem>
                <MenuItem value="2">Completed</MenuItem>
              </Select>
            </FormControl>
            <TextField 
              label="Reference Number" 
              variant="outlined" 
              value={referenceNoFilter} 
              onChange={(e) => setReferenceNoFilter(e.target.value)} 
              sx={{ minWidth: 150 }}
            />
            <TextField label="Start Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={startDate} onChange={e => setStartDate(e.target.value)} />
            <TextField label="End Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={endDate} onChange={e => setEndDate(e.target.value)} />

          </Box>
        )}
      </Box>

      {/* Mobile Card View */}
      {isMobile ? (
        <Box>
          {paginatedData && paginatedData.length > 0 ? (
            paginatedData.map((row) => (
              <MobileCardView key={row.id} row={row} />
            ))
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography>No data available</Typography>
            </Box>
          )}
        </Box>
      ) : (
        /* Desktop Table View */
        <TableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: 600, 
            overflow: 'auto',
            '& .MuiTable-root': {
              minWidth: 1200, // Ensure minimum width for all columns
            }
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {isAdmin && (
                  <TableCell sx={{ minWidth: 100 }}>Actions</TableCell>
                )}
                <TableCell sx={{ minWidth: 120 }}>Reference Number</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Batch Number</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Product</TableCell>
                <TableCell sx={{ minWidth: 80 }}>Quantity</TableCell>
                <TableCell sx={{ minWidth: 100 }}>BOM Dia Qty</TableCell>
                <TableCell sx={{ minWidth: 100 }}>BOM CLR Qty</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Issue Total Qty</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Stone Weight</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Gross Weight</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Net Weight</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Net Weight Purity</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Warehouse</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Vendor Site</TableCell>
                <TableCell sx={{ minWidth: 100 }}>JO Number</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Production Route</TableCell>
                <TableCell sx={{ minWidth: 140 }}>HM Product Category</TableCell>
                <TableCell sx={{ minWidth: 100 }}>HM Purity</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Created By</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Created At</TableCell>
                <TableCell sx={{ minWidth: 100 }}>JO Date</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Batch Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData && paginatedData.length > 0 ? (
                paginatedData.map((row) => (
                  <TableRow key={row.id} hover>
                    {isAdmin && (
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton size="small" onClick={() => onEdit && onEdit(row)}>
                            <EditIcon fontSize="small" sx={{ color: '#2196f3' }}/>
                          </IconButton>
                          <IconButton size="small" onClick={() => onDelete && onDelete(row.id)}>
                            <DeleteIcon fontSize="small" sx={{ color: '#f44336' }}/>
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>{row.reference_number || '-'}</TableCell>
                    <TableCell>{row.batch_number || '-'}</TableCell>
                    <TableCell>{row.product || '-'}</TableCell>
                    <TableCell>{row.qty || '-'}</TableCell>
                    <TableCell>{row.bom_dia_qty || '-'}</TableCell>
                    <TableCell>{row.bom_clr_qty || '-'}</TableCell>
                    <TableCell>{row.issue_tot_qty || '-'}</TableCell>
                    <TableCell>{row.stone_weight || '-'}</TableCell>
                    <TableCell>{row.gross_weight || '-'}</TableCell>
                    <TableCell>{row.net_weight || '-'}</TableCell>
                    <TableCell>{row.net_weight_purity || '-'}</TableCell>
                    <TableCell>{row.warehouse || '-'}</TableCell>
                    <TableCell>{row.vendor_site || '-'}</TableCell>
                    <TableCell>{row.jo_number || '-'}</TableCell>
                    <TableCell>{row.production_route || '-'}</TableCell>
                    <TableCell>{row.hm_product_category || '-'}</TableCell>
                    <TableCell>{row.hm_purity || '-'}</TableCell>
                    <TableCell>{row.createdby || '-'}</TableCell>
                    <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</TableCell>
                    <TableCell>{row.jo_date ? new Date(row.jo_date).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      {row.batch_no_status === 0 ? 'Pending' : 
                       row.batch_no_status === 1 ? 'In Progress' : 
                       row.batch_no_status === 2 ? 'Completed' : '-'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 21 : 20} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Responsive Pagination */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: 2,
        '& .MuiTablePagination-root': {
          overflow: 'visible'
        }
      }}>
        <TablePagination
          component="div"
          count={filteredData?.length || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={isSmallScreen ? [5, 10] : [5, 10, 25, 50]}
          labelRowsPerPage={isSmallScreen ? "Rows:" : "Rows per page:"}
        />
      </Box>
    </Box>
  );
};

export default ImportedInventoryTable; 